import React from 'react';
import {Box, Stack} from '@mui/material';
import {Body, Label, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {
  NegotiationSnapshot,
  NegotiationSnapshotState,
} from '@/types/negotiationSnapshot';
import styles from './NegotiationSnapshotCard.module.scss';

const STATE_LABELS: Record<NegotiationSnapshotState, string> = {
  pending: 'Offer Sent',
  accepted: 'Offer Accepted',
  withdrawn: 'Offer Withdrawn',
  rejected: 'Offer Rejected',
};

const STATE_COLORS: Record<NegotiationSnapshotState, string> = {
  pending: designSystemToken('semantic.fg.warning'),
  accepted: designSystemToken('semantic.fg.success'),
  withdrawn: designSystemToken('semantic.fg.secondary'),
  rejected: designSystemToken('semantic.fg.danger'),
};

const DELIVERABLE_LABELS: Record<string, string> = {
  amazon_post: 'Amazon Posts',
  blog_post: 'Blog Posts',
  blogs_to_upload: 'Blogs',
  facebook_post: 'Facebook Posts',
  instagram_post: 'Instagram Posts',
  instagram_reel_post: 'Instagram Reel Posts',
  instagram_story_post: 'Instagram Story Posts',
  linkedin_post: 'LinkedIn Posts',
  minimum_child_content_submission_count: 'Min # of Story Frames',
  mixed_media_to_upload: 'Story Uploads',
  photos_to_post: 'Posts',
  photos_to_upload: 'Photos',
  pinterest_post: 'Pinterest Posts',
  reddit_post: 'Reddit Posts',
  reels_to_post: 'Instagram Reels Posts',
  review_post: 'Review Posts',
  snapchat_post: 'Snapchat Posts',
  stories_to_post: 'Stories Posts',
  tiktok_post: 'TikTok Posts',
  twitch_post: 'Twitch Posts',
  twitter_post: 'Twitter Posts',
  videos_to_upload: 'Videos',
  youtube_post: 'YouTube Posts',
  minimum_number_of_mixed_media: 'Min # of Mixed Media',
};

function formatDeliverableKind(kind: string): string {
  return DELIVERABLE_LABELS[kind] ?? kind.replace(/_/g, ' ');
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

type NegotiationSnapshotCardProps = {
  snapshot: NegotiationSnapshot;
};

function NegotiationSnapshotCard({snapshot}: NegotiationSnapshotCardProps) {
  return (
    <Stack className={styles.negotiationCard}>
      <Label size="md">{translate('Proposed a Change To Terms')}</Label>

      <Stack className={styles.negotiationSection}>
        <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
          {translate('Payment')}
        </Body>
        <Stack direction="row" className={styles.negotiationPayment}>
          {snapshot.previousCostInCents !== snapshot.costInCents ? (
            <Body size="md" className={styles.negotiationOldValue}>
              {formatCents(snapshot.previousCostInCents)}
            </Body>
          ) : null}
          <Label size="lg">{formatCents(snapshot.costInCents)}</Label>
        </Stack>
      </Stack>

      {snapshot.gigDeliverableChanges.length > 0 ? (
        <Stack className={styles.negotiationSection}>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Deliverables')}
          </Body>
          <Stack direction="row" className={styles.negotiationDeliverables}>
            {snapshot.gigDeliverableChanges.map((change) => (
              <Body size="md" key={change.kind}>
                {change.newRequiredCount}{' '}
                {change.displayText ?? formatDeliverableKind(change.kind)}
              </Body>
            ))}
          </Stack>
        </Stack>
      ) : null}

      <Box className={styles.negotiationStatus}>
        <Body size="sm" color={STATE_COLORS[snapshot.state]}>
          {translate(STATE_LABELS[snapshot.state])}
        </Body>
      </Box>
    </Stack>
  );
}

export default NegotiationSnapshotCard;
